import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { FileDownload as FileDownloadIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { getDynamicCollectionData } from '../../api/DynamicCollections';
import { iBaseCardWithComponentProps, iBaseInnerCardProps } from '../types';
import { CardWithBaseComponent } from '../common/BaseCard';
import Events from '../../events';
import { EventsType } from '../../events-types';
import { exportToExcel, exportToExcelSelectedRows } from '../../utils/export_xlsx';
import { CellEventMapping, createCellsWithEvents, getColumns } from '../../utils/tables';

interface FilterType {
    property: string;
    value: string | number;
    operator?: string;
}

interface DynamicCollectionTableProps extends iBaseInnerCardProps {
    collectionName: string;
    eventGroupToTrigger?: string;
}

const _DynamicCollectionTable: React.FC<DynamicCollectionTableProps> = ({
                                                                            collectionName,
                                                                            setIsShow,
                                                                            setData,
                                                                            data,
                                                                            setColumnFilters,
                                                                            columnFilters,
                                                                            isActive,
                                                                        }) => {
    const UUID = `_DynamicCollectionTable_${collectionName}`;
    const [localData, setLocalData] = useState<any[]>([]);
    const [columns, setColumns] = useState<MRT_ColumnDef<any>[]>([]);

    const fetchData = useCallback(async (searchParams: { filters: FilterType[] }) => {
        try {
            const items = await getDynamicCollectionData(collectionName, searchParams);
            setLocalData(items);
            setData?.(items);

            const show = items.length > 0;
            setIsShow?.(show);

            if (show) {
                const sample = Object.fromEntries(
                    Object.entries(items[0]).filter(([key]) => !key.startsWith('_'))
                );
                const eventMapping: CellEventMapping = {
                    INCHIKEY: EventsType.SEARCH_BY_INCHIKEY,
                    CAS: EventsType.SEARCH_BY_CAS,
                };
                setColumns(getColumns(sample, 2, createCellsWithEvents(eventMapping)));
            } else {
                setColumns([]);
            }
        } catch (error) {
            console.error('Failed to fetch dynamic collection', error);
            setIsShow?.(true); // Show empty card still
        }
    }, [collectionName, setData, setIsShow]);

    const handleEvent = useCallback((event: { detail: any }, type: EventsType) => {
        if (!event.detail) return;

        if (!isActive && [EventsType.SEARCH, EventsType.SEARCH_BY_INCHIKEY, EventsType.SEARCH_BY_CAS].includes(type)) {
            return;
        }

        if (type === EventsType.SEARCH && typeof event.detail === 'string') {
            fetchData({
                filters: [
                    { property: '__ALL_FIELDS__', value: event.detail, operator: 'REGEX' }
                ]
            });
        } else if (type === EventsType.SEARCH_BY_INCHIKEY) {
            fetchData({
                filters: [
                    { property: 'INCHIKEY', value: event.detail, operator: '==' }
                ]
            });
        } else if (type === EventsType.SEARCH_BY_CAS) {
            fetchData({
                filters: [
                    { property: 'CAS', value: event.detail, operator: '==' }
                ]
            });
        } else if (type === EventsType.SEARCH_DYNAMIC && event.detail.collectionName === collectionName) {
            const filters = Array.isArray(event.detail.filters)
                ? event.detail.filters.map((f: any) => ({
                    property: f.property,
                    value: f.value,
                    operator: f.operator || '==',
                }))
                : [];

            fetchData({ filters });
        } else if (type === EventsType.BUCKET_RELOAD_OBJECT && event.detail.HEADER === collectionName) {
            const payload = event.detail.DATA ?? [];
            setLocalData(payload);
            setData?.(payload);
            setColumnFilters?.(event.detail.filter || []);
            setIsShow?.(true);

            if (payload.length > 0) {
                const sample = Object.fromEntries(
                    Object.entries(payload[0]).filter(([key]) => !key.startsWith('_'))
                );
                const eventMapping: CellEventMapping = {
                    INCHIKEY: EventsType.SEARCH_BY_INCHIKEY,
                    CAS: EventsType.SEARCH_BY_CAS,
                };
                setColumns(getColumns(sample, 2, createCellsWithEvents(eventMapping)));
            } else {
                setColumns([]);
            }
        }
    }, [collectionName, fetchData, setData, setColumnFilters, setIsShow, isActive]);

    useEffect(() => {
        const eventHandlers = [
            { type: EventsType.SEARCH, handler: (e: any) => handleEvent(e, EventsType.SEARCH) },
            { type: EventsType.SEARCH_BY_INCHIKEY, handler: (e: any) => handleEvent(e, EventsType.SEARCH_BY_INCHIKEY) },
            { type: EventsType.SEARCH_BY_CAS, handler: (e: any) => handleEvent(e, EventsType.SEARCH_BY_CAS) },
            { type: EventsType.SEARCH_DYNAMIC, handler: (e: any) => handleEvent(e, EventsType.SEARCH_DYNAMIC) },
            { type: EventsType.BUCKET_RELOAD_OBJECT, handler: (e: any) => handleEvent(e, EventsType.BUCKET_RELOAD_OBJECT) },
        ];

        eventHandlers.forEach(({ type, handler }) => {
            Events.on(type, handler, UUID);
        });

        return () => {
            eventHandlers.forEach(({ type }) => Events.off(type, UUID));
        };
    }, [handleEvent]);

    const handleFilterFnChange = (filters: any) => {
        setColumnFilters?.(filters);
    };

    return (
        <MaterialReactTable
            columns={columns}
            data={localData ?? []}
            enableColumnFilterModes
            enableRowSelection
            enableColumnOrdering
            enableGlobalFilter={false}
            enableDensityToggle
            initialState={{ density: 'compact' }}
            onColumnFiltersChange={handleFilterFnChange}
            state={{ columnFilters }}
            enableRowActions
            renderRowActions={({ row }) => (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                    <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => {
                           Events.trigger(EventsType.OPEN_DYNAMIC_OBJECT, row.original);
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </Box>
            )}
            positionToolbarAlertBanner="bottom"
            renderTopToolbarCustomActions={({ table }) => (
                <Box sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}>
                    <Button
                        color="primary"
                        onClick={() => exportToExcel(localData, collectionName)}
                        startIcon={<FileDownloadIcon />}
                        variant="contained"
                    >
                        Export All Data
                    </Button>
                    <Button
                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        onClick={() => exportToExcelSelectedRows(table.getSelectedRowModel().rows, collectionName)}
                        startIcon={<FileDownloadIcon />}
                        variant="contained"
                    >
                        Export Selected Rows
                    </Button>
                </Box>
            )}
        />
    );
};

const DynamicCollectionTable: React.FC<Omit<iBaseCardWithComponentProps, 'cardComponent'> & { collectionName: string }> = ({
                                                                                                                               collectionName,
                                                                                                                               header,
                                                                                                                               width,
                                                                                                                               eventGroupToTrigger,
                                                                                                                           }) => {
    return (
        <CardWithBaseComponent
            cardComponent={(props) => (
                <_DynamicCollectionTable {...props} collectionName={collectionName} eventGroupToTrigger={eventGroupToTrigger} />
            )}
            header={header ?? collectionName}
            width={width}
            eventGroupToTrigger={eventGroupToTrigger}
        />
    );
};

export default React.memo(DynamicCollectionTable);
